import { Component, createElement } from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import {
  setNewsletterVisibility as setNewsletterVisibilityForDispatch,
  setRegionalVisibility as setRegionalVisibilityForDispatch,
} from '../../modules/manageModal';
import Store from '../../common/models/Store';
import { ModalAutoDisplayerProps, ModalAutoDisplayerState } from './interfaces';
import { modals, scrollEvent, visitEvent } from './modalsConfig';

/* Helpers */
const mapStateToProps = ({
  api: { countryId },
  modals: {
    internalNewsletter: { visible: newsletterModalVisible },
    internalRegional: { visible: regionalModalVisible },
  },
}: Store) => ({
  countryId,
  newsletterModalVisible,
  regionalModalVisible,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  setNewsletterVisibility: (value: boolean) => dispatch(setNewsletterVisibilityForDispatch(value)),
  setRegionalVisibility: (value: boolean) => dispatch(setRegionalVisibilityForDispatch(value)),
});

const getScrollPercentage = () => {
  const h = document.documentElement;
  const b = document.body;
  const st = 'scrollTop';
  const sh = 'scrollHeight';
  return ((h[st] || b[st]) / ((h[sh] || b[sh]) - h.clientHeight)) * 100;
};

class ModalAutoDisplayer extends Component<ModalAutoDisplayerProps, ModalAutoDisplayerState> {
  constructor(props: ModalAutoDisplayerProps) {
    super(props);

    // for handling of situations when cookies are turned off
    this.state = { modalsAutoDisplayEnabled: { newsletter: true, regional: true } };
    ModalAutoDisplayer.allAllowedCountriesSet = Object.values(modals).reduce((set, { allowedCountries }) => {
      allowedCountries.forEach(country => set.add(country));
      return set;
    }, new Set()) as Set<string>;
  }

  componentDidMount() {
    if (this.checkIfShouldDisplaySomeModalsInThisCountry() && document) {
      document.addEventListener(scrollEvent, this.displayModalsIfConditionsMet);
      this.displayModalsIfConditionsMet();
    }
  }

  getModalsAutoDisplayPermissions = () => {
    const modalsRememberedInCookies = {};

    Object.entries(modals).forEach(([name, { checkIfCookieSet }]) => {
      if (checkIfCookieSet()) {
        modalsRememberedInCookies[name] = true;
      }
    });

    const { modalsAutoDisplayEnabled } = this.state;
    const allowedModals = { ...modalsAutoDisplayEnabled };
    Object.keys(modalsAutoDisplayEnabled).forEach(key => {
      if (Object.prototype.hasOwnProperty.call(modalsRememberedInCookies, key)) {
        allowedModals[key] = false;
      }
    });

    return allowedModals;
  };

  // returns new modal auto display value to be set
  handleScrollEvent = ({ scrollPercentage, display, maxOncePerPageView }) => {
    if (scrollPercentage && getScrollPercentage() > scrollPercentage) {
      return this.displayModal(display, maxOncePerPageView);
    }
    return true;
  };

  // returns new modal auto display value to be set
  handleVisitEvent = ({ display, maxOncePerPageView }) => {
    this.displayModal(display, maxOncePerPageView);
  };

  displayModalsIfConditionsMet = () => {
    const { countryId } = this.props;
    const modalsPermissions = this.getModalsAutoDisplayPermissions();

    if (this.isAnyInternalModalDisplayed()) {
      return;
    }

    Object.entries(modalsPermissions).forEach(([modalName, allowed]) => {
      if (allowed && Object.prototype.hasOwnProperty.call(modals, modalName)) {
        const { [modalName]: modalConfig } = modals;
        const { allowedCountries, event } = modalConfig;

        if (allowedCountries.includes(countryId.toString())) {
          switch (event) {
            case scrollEvent:
              modalsPermissions[modalName] = this.handleScrollEvent(modalConfig);
              break;
            case visitEvent:
              modalsPermissions[modalName] = this.handleVisitEvent(modalConfig);
              break;
            default:
              break;
          }
        }
      }
    });

    this.setState({ modalsAutoDisplayEnabled: modalsPermissions });
  };

  // returns new modal auto display value to be set
  displayModal = (
    display: (props: ModalAutoDisplayerProps) => void,
    shouldModalBeDisplayedOnlyOncePerPageView: boolean,
  ) => {
    display(this.props);
    return shouldModalBeDisplayedOnlyOncePerPageView;
  };

  checkIfShouldDisplaySomeModalsInThisCountry = () => {
    const { countryId } = this.props;
    return ModalAutoDisplayer.allAllowedCountriesSet.has(countryId.toString());
  };

  static allAllowedCountriesSet: Set<string>;

  isAnyInternalModalDisplayed() {
    const { newsletterModalVisible, regionalModalVisible } = this.props;
    return newsletterModalVisible || regionalModalVisible;
  }

  render() {
    if (!this.checkIfShouldDisplaySomeModalsInThisCountry()) {
      return null;
    }

    return Object.values(modals).map(({ component: modalComponent, close, checkIfShouldBeDisplayed }) => {
      if (modalComponent) {
        return checkIfShouldBeDisplayed(this.props)
          ? createElement(modalComponent, {
              ...this.props,
              close: () => {
                close(this.props);
              },
            })
          : null;
      }
      return null;
    });
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ModalAutoDisplayer);
